import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, Loader, AlertCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking"); // checking, paid, failed
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const pollStatus = async () => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = res.data;
        setPaymentData(data);

        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        } else if (data.status === "expired") {
          setStatus("failed");
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, pollInterval);
        } else {
          setStatus("paid"); // Assume success if stripe redirected here
        }
      } catch (err) {
        console.error("Status check error:", err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, pollInterval);
        } else {
          setStatus("failed");
        }
      }
    };

    pollStatus();
  }, [sessionId]);

  return (
    <div
      data-testid="payment-success-page"
      className="min-h-screen bg-[#F9F6F0] flex items-center justify-center px-6"
    >
      <div className="max-w-md w-full text-center">
        {status === "checking" && (
          <div className="animate-fade-in-up">
            <Loader size={48} className="text-[#CC5543] mx-auto mb-6 animate-spin" />
            <h1 className="font-['Cormorant_Garamond'] text-3xl text-[#1A3626] mb-3">
              Confirming Payment
            </h1>
            <p className="text-sm text-[#596B5B]">
              Please wait while we verify your payment...
            </p>
          </div>
        )}

        {status === "paid" && (
          <div className="animate-fade-in-up">
            <div className="w-20 h-20 rounded-full bg-[#1A3626]/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-[#1A3626]" />
            </div>
            <h1 className="font-['Cormorant_Garamond'] text-4xl text-[#1A3626] mb-3">
              You're Booked!
            </h1>
            <p className="text-sm text-[#596B5B] mb-2">
              Your spot in the Weekly Afro-Cuban Dance Class has been secured.
            </p>
            {paymentData && (
              <p className="text-xs text-[#8C9D88] mb-8">
                Amount: ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
              </p>
            )}
            <div className="bg-[#EBE6DF] border border-[#DCD6CC] rounded-lg p-6 mb-8 text-left">
              <h3 className="font-['Cormorant_Garamond'] text-xl text-[#1A3626] mb-3">
                What's Next
              </h3>
              <ul className="space-y-2 text-sm text-[#596B5B]">
                <li className="flex items-start gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#CC5543] mt-2 flex-shrink-0" />
                  Wear comfortable clothing you can move freely in
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#CC5543] mt-2 flex-shrink-0" />
                  Bring a water bottle and a towel
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#CC5543] mt-2 flex-shrink-0" />
                  Arrive 10 minutes early to settle in
                </li>
              </ul>
            </div>
            <button
              data-testid="return-home-btn"
              onClick={() => navigate("/")}
              className="px-8 py-3.5 bg-[#CC5543] text-white text-xs uppercase tracking-[0.2em] font-medium rounded-full hover:bg-[#A94334] transition-all duration-300"
            >
              Return Home
            </button>
          </div>
        )}

        {status === "failed" && (
          <div className="animate-fade-in-up">
            <div className="w-20 h-20 rounded-full bg-[#CC5543]/10 flex items-center justify-center mx-auto mb-6">
              <AlertCircle size={40} className="text-[#CC5543]" />
            </div>
            <h1 className="font-['Cormorant_Garamond'] text-3xl text-[#1A3626] mb-3">
              Something Went Wrong
            </h1>
            <p className="text-sm text-[#596B5B] mb-8">
              We couldn't confirm your payment. Please try again or contact us for help.
            </p>
            <button
              data-testid="try-again-btn"
              onClick={() => navigate("/")}
              className="px-8 py-3.5 bg-[#CC5543] text-white text-xs uppercase tracking-[0.2em] font-medium rounded-full hover:bg-[#A94334] transition-all duration-300"
            >
              Go Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
